// Web transcript (MYC-090): the my.echoflicks.com creation timeline of a web
// Echo, read from dashboard-api `GET /echoes/{workload_id}/web-transcript`, and
// turned into display rows so an operator can debug a web Echo end-to-end
// without AWS. Pure — no React, no I/O.
//
// Wire contract: plans/2026-10-08-web-create/implementation-contract.md
//   C5  timeline event kinds and payloads (the envelope {seq, kind, created_at,
//       payload} is shared by the API and the DynamoDB EVT# items),
//   C6  `web.question.view.v1` (the felix.question payload),
//   C13 the dashboard-api route. 200 = `web-transcript.v1` {schema_version,
//       workload_id, creation_id, status, awaiting, created_at, updated_at,
//       last_event_seq, event_count, truncated, events} (events in seq order,
//       capped at 1000); 404 {error:{code:"not_found", reason}} with reason
//       not_web_echo | creation_not_found | web_creations_table_unbound — every
//       404 means "no web transcript" and the panel hides.
// Golden fixtures: ./fixtures/web_create/ (byte-identical, SHA256SUMS.json).
//
// Privacy: the timeline carries no owner phone number (C5/C6). This mapper only
// reads the documented fields, and an unknown kind shows its payload KEYS, never
// its values, so a field the bot adds later cannot leak into the CRM unseen.

export type WebTextNode =
  | { t: "text"; v: string }
  | { t: "strong"; v: string }
  | { t: "em"; v: string }
  | { t: "br" };

export type WebTranscriptEvent = {
  seq: number;
  kind: string;
  created_at: string | null;
  payload: Record<string, unknown>;
};

export type EchoWebTranscript = {
  creation_id: string | null;
  /** Web creation status (C7): collecting … delivered/failed/cancelled/expired. */
  status: string | null;
  events: WebTranscriptEvent[];
  // The rest of `web-transcript.v1`; parseWebTranscript always fills them.
  schema_version?: string | null;
  workload_id?: string | null;
  /** Who the creation waits on (C7): none | felix | user | media | payment. */
  awaiting?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  last_event_seq?: number | null;
  /** Events in the timeline; more than `events.length` when truncated. */
  event_count?: number | null;
  /** The route caps the events at 1000. */
  truncated?: boolean;
};

/** Terminal web creation statuses (C7): they absorb every later input. */
export const WEB_CREATION_TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  "delivered",
  "failed",
  "cancelled",
  "expired",
]);

export const isTerminalWebCreationStatus = (status: string | null) =>
  status != null && WEB_CREATION_TERMINAL_STATUSES.has(status);

// ---------------------------------------------------------------------------
// Display rows
// ---------------------------------------------------------------------------

type RowBase = {
  key: string;
  seq: number;
  at: string | null;
};

export type WebTranscriptOption = {
  choiceId: string | null;
  position: number | null;
  label: string;
  /** The option the following `user.answer` picked. */
  chosen: boolean;
};

export type WebStatusTone =
  | "neutral"
  | "progress"
  | "success"
  | "warning"
  | "error";

export type WebTranscriptRow =
  | (RowBase & {
      type: "felix_question";
      promptId: string | null;
      questionId: string | null;
      category: string;
      categoryLabel: string;
      /** director | canon | classifier | none (C6). */
      categorySource: string | null;
      /** single_choice | open | confirmation | identity (C6). */
      questionKind: string | null;
      stem: WebTextNode[];
      options: WebTranscriptOption[];
      allowsFreeText: boolean;
      /** `beat_progress` when the question is a beat (index is null otherwise). */
      beat: { index: number; of: number | null } | null;
      /** Identity questions: the cast label (or group id) being confirmed. */
      identityLabel: string | null;
      /** A later `user.answer` named this prompt. */
      answered: boolean;
    })
  | (RowBase & {
      type: "user_answer";
      /** choice | free_text | assist_chip | identity (C5). */
      mode: string;
      modeLabel: string;
      text: string | null;
      promptId: string | null;
      /** The picked option's label, resolved from the question it answers. */
      choiceLabel: string | null;
      assistChipId: string | null;
    })
  | (RowBase & {
      type: "felix_say";
      tone: "info" | "system" | "error";
      text: WebTextNode[];
    })
  | (RowBase & { type: "user_story"; text: string | null })
  | (RowBase & {
      type: "felix_media_request";
      reason: string | null;
      min: number | null;
      max: number | null;
    })
  | (RowBase & {
      type: "user_media";
      count: number | null;
      note: string | null;
    })
  | (RowBase & {
      type: "status";
      kind: string;
      tone: WebStatusTone;
      label: string;
      detail: string | null;
    })
  | (RowBase & { type: "unknown"; kind: string; payloadKeys: string[] });

export type WebTranscriptSummary = {
  questionCount: number;
  answeredCount: number;
  eventCount: number;
};

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<string, string> = {
  vision: "Vision",
  tone: "Tone",
  stars: "Stars",
  identity: "Identity",
  turning_point: "Turning point",
  soundtrack: "Soundtrack",
  narration: "Narration",
  voice: "Voice",
  title_cards: "Title cards",
  style: "Style",
  title: "Title",
  confirmation: "Confirmation",
  media_request: "Media request",
  clarification: "Clarification",
  system: "System",
  other: "Other",
};

const ANSWER_MODE_LABELS: Record<string, string> = {
  choice: "Choice",
  free_text: "Free text",
  assist_chip: "Assist chip",
  identity: "Identity",
};

function humanize(value: string): string {
  const spaced = value.replace(/[_.]+/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : value;
}

export function webCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? humanize(category);
}

export function webAnswerModeLabel(mode: string): string {
  return ANSWER_MODE_LABELS[mode] ?? humanize(mode);
}

// ---------------------------------------------------------------------------
// Field readers
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value : null;

const integer = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) ? value : null;

/**
 * The text AST of `stem` / `felix.say.text` (C5): text, strong, em and br nodes.
 * Never HTML. A bare string (a defensive legacy shape) becomes one text node;
 * malformed nodes are dropped rather than rendered.
 */
export function toWebTextAst(value: unknown): WebTextNode[] {
  if (typeof value === "string") {
    return value ? [{ t: "text", v: value }] : [];
  }
  if (!Array.isArray(value)) return [];
  const nodes: WebTextNode[] = [];
  for (const node of value) {
    if (!isRecord(node)) continue;
    if (node.t === "br") {
      nodes.push({ t: "br" });
      continue;
    }
    if (
      (node.t === "text" || node.t === "strong" || node.t === "em") &&
      typeof node.v === "string"
    ) {
      nodes.push({ t: node.t, v: node.v });
    }
  }
  return nodes;
}

/** Plain text of an AST (br → newline), for copy and tests. */
export function webTextAstToPlain(nodes: WebTextNode[]): string {
  return nodes.map((node) => (node.t === "br" ? "\n" : node.v)).join("");
}

// ---------------------------------------------------------------------------
// API response
// ---------------------------------------------------------------------------

function parseEvent(value: unknown): WebTranscriptEvent | null {
  if (!isRecord(value)) return null;
  const kind = text(value.kind);
  if (!kind) return null;
  const seq = integer(value.seq);
  return {
    seq: seq != null && seq >= 1 ? seq : 0,
    kind,
    created_at: text(value.created_at),
    payload: isRecord(value.payload) ? value.payload : {},
  };
}

/**
 * Parse the dashboard-api `web-transcript.v1` response. A `{data: …}` wrapper
 * (the convention of other dashboard routes) is accepted too. Returns null for
 * anything that is not a transcript object.
 */
export function parseWebTranscript(value: unknown): EchoWebTranscript | null {
  const body = isRecord(value) && isRecord(value.data) ? value.data : value;
  if (!isRecord(body)) return null;
  const rawEvents = Array.isArray(body.events) ? body.events : [];
  const events = rawEvents
    .map(parseEvent)
    .filter((event): event is WebTranscriptEvent => event !== null);
  const eventCount = integer(body.event_count);
  return {
    schema_version: text(body.schema_version),
    workload_id: text(body.workload_id),
    creation_id: text(body.creation_id),
    status: text(body.status),
    awaiting: text(body.awaiting),
    created_at: text(body.created_at),
    updated_at: text(body.updated_at),
    last_event_seq: integer(body.last_event_seq),
    event_count: eventCount,
    truncated:
      body.truncated === true ||
      (eventCount != null && eventCount > events.length),
    events,
  };
}

// ---------------------------------------------------------------------------
// Events → rows
// ---------------------------------------------------------------------------

/** Order by `seq` (0 = missing, kept last in arrival order); drop duplicate seqs. */
function orderedEvents(events: WebTranscriptEvent[]): WebTranscriptEvent[] {
  const seen = new Set<number>();
  const sequenced: WebTranscriptEvent[] = [];
  const unsequenced: WebTranscriptEvent[] = [];
  for (const event of events) {
    if (event.seq <= 0) {
      unsequenced.push(event);
      continue;
    }
    if (seen.has(event.seq)) continue;
    seen.add(event.seq);
    sequenced.push(event);
  }
  sequenced.sort((left, right) => left.seq - right.seq);
  return [...sequenced, ...unsequenced];
}

function questionRow(
  event: WebTranscriptEvent,
  base: RowBase,
): Extract<WebTranscriptRow, { type: "felix_question" }> {
  const payload = event.payload;
  const category = text(payload.category) ?? "other";
  const beatProgress = isRecord(payload.beat_progress)
    ? payload.beat_progress
    : null;
  const beatIndex = integer(beatProgress?.index);
  const identity = isRecord(payload.identity) ? payload.identity : null;
  const options = (Array.isArray(payload.options) ? payload.options : [])
    .filter(isRecord)
    .map(
      (option): WebTranscriptOption => ({
        choiceId: text(option.choice_id),
        position: integer(option.position),
        label: text(option.label) ?? "",
        chosen: false,
      }),
    )
    .sort(
      (left, right) =>
        (left.position ?? Number.MAX_SAFE_INTEGER) -
        (right.position ?? Number.MAX_SAFE_INTEGER),
    );
  return {
    ...base,
    type: "felix_question",
    promptId: text(payload.prompt_id),
    questionId: text(payload.question_id),
    category,
    categoryLabel: webCategoryLabel(category),
    categorySource: text(payload.category_source),
    questionKind: text(payload.kind),
    stem: toWebTextAst(payload.stem),
    options,
    allowsFreeText: payload.allows_free_text === true,
    beat:
      beatIndex != null
        ? { index: beatIndex, of: integer(beatProgress?.of) }
        : null,
    identityLabel: identity
      ? (text(identity.label) ?? text(identity.group_id))
      : null,
    answered: false,
  };
}

function joinDetail(parts: (string | null | undefined)[]): string | null {
  const kept = parts.filter((part): part is string => Boolean(part));
  return kept.length ? kept.join(" · ") : null;
}

function statusRow(
  event: WebTranscriptEvent,
  base: RowBase,
): WebTranscriptRow | null {
  const payload = event.payload;
  const make = (
    tone: WebStatusTone,
    label: string,
    detail: string | null = null,
  ): WebTranscriptRow => ({
    ...base,
    type: "status",
    kind: event.kind,
    tone,
    label,
    detail,
  });
  switch (event.kind) {
    case "creation.started":
      return make(
        "neutral",
        "Creation started",
        text(payload.occasion_label) ?? text(payload.occasion_key),
      );
    case "felix.watching":
      return make("progress", "Felix is reviewing the footage");
    case "production.started":
      return make("progress", "Production started");
    case "production.poster":
      return make("progress", "Poster ready", text(payload.title));
    case "production.queue": {
      const position = integer(payload.position);
      return make(
        "progress",
        "Waiting in the render queue",
        joinDetail([
          position != null ? `position ${position}` : null,
          text(payload.eta_band) ? `ETA ${text(payload.eta_band)}` : null,
        ]),
      );
    }
    case "premiere.ready":
      return make("success", "Premiere ready", text(payload.title));
    case "production.failed": {
      const code = text(payload.code);
      const refunded =
        typeof payload.refunded === "boolean"
          ? payload.refunded
            ? "refunded"
            : "not refunded"
          : null;
      return make(
        "error",
        "Production failed",
        joinDetail([code ? `code ${code}` : null, refunded]),
      );
    }
    case "felix.stalled":
      return make(
        "warning",
        payload.stage === "render"
          ? "Stalled: the render stopped moving"
          : payload.stage === "felix"
            ? "Stalled: waiting on Felix"
            : "Stalled",
      );
    case "creation.cancelled":
      return make(
        "warning",
        payload.by === "operator"
          ? "Cancelled by an operator"
          : payload.by === "user"
            ? "Cancelled by the user"
            : "Cancelled",
      );
    case "creation.expired":
      return make("warning", "Draft expired");
    default:
      return null;
  }
}

/**
 * Turn the timeline into display rows, in `seq` order. Each `felix.question`
 * marks the option the following `user.answer` (same `prompt_id`) picked, and
 * the answer row carries that option's label. Unknown kinds become a neutral
 * row that names the kind and its payload keys (never values) — never a crash.
 */
export function buildWebTranscriptRows(
  events: WebTranscriptEvent[],
): WebTranscriptRow[] {
  const rows: WebTranscriptRow[] = [];
  // prompt_id → the latest still-unanswered question row with that prompt.
  const openQuestions = new Map<
    string,
    Extract<WebTranscriptRow, { type: "felix_question" }>
  >();

  orderedEvents(events).forEach((event, index) => {
    const base: RowBase = {
      key: event.seq > 0 ? `seq-${event.seq}` : `event-${index}`,
      seq: event.seq,
      at: event.created_at,
    };
    const payload = event.payload;

    switch (event.kind) {
      case "felix.question": {
        const row = questionRow(event, base);
        if (row.promptId) openQuestions.set(row.promptId, row);
        rows.push(row);
        return;
      }
      case "user.answer": {
        const promptId = text(payload.prompt_id);
        const choiceId = text(payload.choice_id);
        const mode = text(payload.mode) ?? "free_text";
        const question = promptId ? openQuestions.get(promptId) : undefined;
        let choiceLabel: string | null = null;
        if (question) {
          question.answered = true;
          openQuestions.delete(promptId as string);
          if (choiceId) {
            for (const option of question.options) {
              if (option.choiceId === choiceId) {
                option.chosen = true;
                choiceLabel = option.label || null;
              }
            }
          }
        }
        rows.push({
          ...base,
          type: "user_answer",
          mode,
          modeLabel: webAnswerModeLabel(mode),
          text: text(payload.text),
          promptId,
          choiceLabel,
          assistChipId: text(payload.assist_chip_id),
        });
        return;
      }
      case "felix.say": {
        const tone =
          payload.tone === "system" || payload.tone === "error"
            ? payload.tone
            : "info";
        rows.push({
          ...base,
          type: "felix_say",
          tone,
          text: toWebTextAst(payload.text),
        });
        return;
      }
      case "user.story":
        rows.push({ ...base, type: "user_story", text: text(payload.text) });
        return;
      case "felix.media_request":
        rows.push({
          ...base,
          type: "felix_media_request",
          reason: text(payload.reason),
          min: integer(payload.min),
          max: integer(payload.max),
        });
        return;
      case "user.media":
        rows.push({
          ...base,
          type: "user_media",
          count: integer(payload.count),
          note: text(payload.note),
        });
        return;
      default: {
        const status = statusRow(event, base);
        rows.push(
          status ?? {
            ...base,
            type: "unknown",
            kind: event.kind,
            payloadKeys: Object.keys(payload).sort(),
          },
        );
      }
    }
  });

  return rows;
}

export function summarizeWebTranscript(
  rows: WebTranscriptRow[],
): WebTranscriptSummary {
  let questionCount = 0;
  let answeredCount = 0;
  for (const row of rows) {
    if (row.type !== "felix_question") continue;
    questionCount += 1;
    if (row.answered) answeredCount += 1;
  }
  return { questionCount, answeredCount, eventCount: rows.length };
}
